import { DateTime } from 'luxon'
import { campaigntrack, realhub } from 'assets/icons'
import config from 'config'

export function encodeParams(options = {}) {
  const esc = encodeURIComponent
  const keys = Object.keys(options)
  const query = []

  keys.map((key) => {
    const value = options[key]

    if (value && Array.isArray(value)) {
      const arrayKey = `${esc(key)}[]`

      value.forEach((valueItem) => {
        query.push(`${arrayKey}=${esc(valueItem)}`)
      })
    } else if (value) {
      const arrayKey = `${esc(key)}`

      query.push(`${arrayKey}=${esc(value)}`)
    } else {
      return null
    }
  })

  return query.join('&')
}

export function previewOrientation(height, width) {
  let orientation = 'portrait'

  if (width >= height) {
    orientation = 'landscape'
  }

  return orientation
}

export function previewScale(orientation, holderSize, width, height) {
  let scale = 1

  if (orientation === 'landscape') {
    scale = (holderSize.width - holderSize.padding * 2) / width
  } else {
    scale = (holderSize.height - holderSize.padding * 2) / height
  }

  return scale
}

export function previewMargins(orientation, holderSize, imageSize) {
  const margins = {
    top: orientation === 'landscape' ? (holderSize.height - imageSize.height) / 2 : holderSize.padding,
    left: orientation === 'portrait' ? (holderSize.width - imageSize.width) / 2 : holderSize.padding,
  }

  return margins
}

export function objectsToArray(objects) {
  let array = []

  if (objects) {
    array = Object.keys(objects).map((key) => objects[key])
  }

  return array
}

export function sortArrayBy(array, direction = 'asc', sortKey = 'title') {
  const isStringSortKey = typeof sortKey === 'string'
  const getSortColumn = (item) => (isStringSortKey ? item[sortKey] : sortKey(item))

  const sorted = array.sort((a, b) => {
    const compareA = getSortColumn(a)?.toUpperCase()
    const compareB = getSortColumn(b)?.toUpperCase()

    if (compareA > compareB) return direction === 'asc' ? 1 : -1
    if (compareA < compareB) return direction === 'asc' ? -1 : 1

    return 0
  })

  return sorted
}

export function sortEntitiesBy(entities, direction, sortKey) {
  const array = Object.values(entities)
  return sortArrayBy(array, direction, sortKey)
}

export function getCookies() {
  const cookies = {}

  const parts = document.cookie.split('; ')
  parts.forEach((part) => {
    const [name, value] = part.split('=')
    cookies[name] = decodeURIComponent(value)
  })

  return cookies
}

export function convertBytesTo(fileSize, outputFormat = 'MB') {
  if (outputFormat === 'MB') {
    const size = fileSize / 1024 ** 2

    return `${Math.round(size * 100) / 100} ${outputFormat}`
  }

  return null
}

export function covertNumberToTwoDigit(value) {
  const valueLength = value.toString().length

  if (valueLength === 0) return '00'

  return valueLength === 1 ? `0${value}` : value.toString()
}

export function dateToFilter(filterDate, filterType) {
  let today = new Date()
  let baseDate = new Date(filterDate)
  let daysOffset = Math.floor(
    (Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()) -
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
    (1000 * 60 * 60 * 24)
  )

  if (filterType === 'end') daysOffset++

  return daysOffset.toString() + 'd'
}

export function filterToDate(filterDate, filterType, userTimeZone) {
  const today = DateTime.now().setZone(userTimeZone)
  let baseDate = today

  const dateFilters = filterDate.split(' ')

  dateFilters.forEach((dateFilter) => {
    const offset = parseInt(dateFilter.slice(0, -1))
    const type = dateFilter.slice(dateFilter.length - 1)

    if (type === 'd') {
      baseDate = baseDate.plus({ days: filterType === 'start' ? offset : offset - 1 })
    }

    if (type === 'w') {
      baseDate = baseDate.plus({ weeks: filterType === 'start' ? offset : offset - 1 })

      if (filterType === 'start') {
        baseDate = baseDate.startOf('week')
      } else {
        baseDate = baseDate.endOf('week')
      }
    }

    if (type === 'm') {
      baseDate = baseDate.plus({ months: filterType === 'start' ? offset : offset - 1 })

      if (filterType === 'start') {
        baseDate = baseDate.startOf('month')
      } else {
        baseDate = baseDate.endOf('month')
      }
    }
  })

  return baseDate
}

export function stringToKey(value) {
  if (value) {
    return value.toLowerCase().replace(/\s/g, '_')
  }

  return ''
}

export function digObject(target, keyString, defaultValue) {
  const keys = keyString.split('.')

  const value = keys.reduce(
    (acc, key) => {
      const accValue = typeof acc === 'undefined' || acc === null ? acc : acc[key]
      return accValue
    },
    { ...target }
  )

  if (value === null || value === undefined) return defaultValue

  return value
}

export function deepSetObject(source, keyString, value) {
  const updated = source ? JSON.parse(JSON.stringify(source)) : {}

  const keys = keyString.split('.')
  const lastKey = keys.pop()

  const lastObject = keys.reduce((acc, key) => {
    acc[key] = acc[key] || {}
    return acc[key]
  }, updated)

  lastObject[lastKey] = value

  return updated
}

function findArrayIndex(array, value, options = {}) {
  const { deepCompare, useObjectKey } = options

  // Replace items with the same key
  if (useObjectKey) {
    return array.findIndex((item) => item[useObjectKey] === value[useObjectKey])
  }

  // Deep Compare - compare objects by stringifying them
  if (deepCompare) {
    return array.findIndex((item) => JSON.stringify(item) === JSON.stringify(value))
  }

  // Simple Array
  return array.findIndex((item) => item === value)
}

export function toggleArray(array, value, options = {}) {
  const { useObjectKey } = options

  const updatedArray = array ? JSON.parse(JSON.stringify([...array])) : []

  const index = findArrayIndex(updatedArray, value, options)

  if (index !== -1) {
    // useObjectKey replaces the original object if match found
    if (useObjectKey) {
      updatedArray.splice(index, 1, value)
      return updatedArray
    }

    updatedArray.splice(index, 1)
  }

  if (index === -1) {
    updatedArray.push(value)
  }

  return updatedArray
}

export function groupBy(array, groupKey, customItem) {
  const isStringGroupKey = typeof groupKey === 'string'
  const grouped = []

  array.forEach((item) => {
    const key = isStringGroupKey ? item[groupKey] : groupKey(item)
    const collection = grouped.find((i) => i.groupKey === key)
    const formattedItem = customItem ? customItem(item) : item

    if (!collection) {
      grouped.push({
        groupKey: key,
        items: [formattedItem],
      })
    } else {
      collection.items.push(formattedItem)
    }
  })

  return grouped
}

export function generateRedirectUrl() {
  if (!window) return null

  const {
    location: { hash, pathname, search },
  } = window

  return encodeURIComponent(pathname + search + hash)
}

export function matchFilterString(string, filterString) {
  if (!string) return false

  if (filterString) {
    return string.toLowerCase().includes(filterString.toLowerCase())
  }
  return true
}

export function matchFilterNumber(number, filterNumber) {
  if (filterNumber) return Number(number) === Number(filterNumber)
  return true
}

export function matchFilterArrayIncludes(array, value) {
  if (!array) return false

  if (value) return array.includes(value)
  return true
}

export function scrollIntoViewWithOffset(element, offset) {
  const top = element.getBoundingClientRect().top + window.pageYOffset + (offset || -85)

  return window.scrollTo({
    behavior: 'smooth',
    top: top,
  })
}

export function trimAndLowerCase(value) {
  const trimString = value.replace(/\s/g, '').toLowerCase()

  return trimString
}

export function formatToDollar(value) {
  const dollars = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  })

  return dollars.format(value)
}

export function getAvailableProviders(bookableSlots, currentTask) {
  const currentBooking = currentTask?.requisites?.find((x) => x.name === 'Booking')

  return bookableSlots.filter((x) => {
    const start = DateTime.fromISO(x.start, { zone: 'utc' }).toISO({ suppressMilliseconds: true })
    const end = DateTime.fromISO(x.end, { zone: 'utc' }).toISO({ suppressMilliseconds: true })

    const taskStart = DateTime.fromISO(currentTask?.value?.start, { zone: 'utc' }).toISO({ suppressMilliseconds: true })
    const taskEnd = DateTime.fromISO(currentTask?.value?.end, { zone: 'utc' }).toISO({ suppressMilliseconds: true })

    return start <= taskStart && end >= taskEnd && currentBooking?.value?.service_types?.every((service) => x.services?.includes(service))
  })
}

export function getNearestProvider(providerSlots) {
  let availableProviders =
    providerSlots?.sort((a, b) => {
      if (!a?.travel_from) return 1
      if (!b?.travel_from) return -1
      return a.travel_from.duration < b.travel_from.duration ? -1 : 1
    }) || []

  return availableProviders.length ? availableProviders[0] : null
}

export function parseValueByType(value, type) {
  switch (type) {
    case 'Boolean':
      return Boolean(value)
    case 'DateTime':
      return value ? DateTime.fromISO(value).toUTC().toString() : value
    case 'TimeRange':
      return value ? value + ':00' : value
    case 'PositiveInteger':
      return parseInt(value, 10)
    case 'Currency':
    case 'Number':
      return parseFloat(value)
    default:
      return value
  }
}

export function getOptionPriceByPartValue(part, partValue) {
  const partOption = part?.detail?.options?.find((option) => option.value === partValue)
  const partIndex = part?.detail?.options?.indexOf(partOption)
  const partPrice = part?.price_tags?.find((price) => price?.option_index === partIndex)

  return partPrice?.unit_price || 0
}

export function getPartValueByType(part) {
  switch (part?.detail?.type) {
    case 'MultiSelect':
      return part?.detail?.options
        ?.filter((x) => part?.detail?.value?.includes(x.value))
        ?.map((x) => x.name)
        ?.join(', ')
    case 'SingleSelect':
      return part?.detail?.options?.find((x) => x.value === part?.detail?.value)?.name
    default:
      return part?.detail?.value || ''
  }
}

export function getUnitPriceByType(part) {
  const flags = part?.detail?.flags || []

  let unitPrice = 0
  switch (part?.detail?.type) {
    case 'MultiSelect':
      let partTotal = 0
      part?.detail?.value?.map((value) => {
        partTotal = partTotal + getOptionPriceByPartValue(part, value) || 0
      })
      unitPrice = partTotal
      break
    case 'SingleSelect':
      unitPrice = getOptionPriceByPartValue(part, part?.detail?.value) || 0
      break
    default:
      unitPrice = part?.detail?.value && part?.detail?.value !== null ? part?.price_tags?.unit_price || 0 : 0
  }

  return unitPrice * (flags.includes('ValueAsQty') && !isNaN(part?.detail?.value) ? part?.detail?.value || 1 : 1)
}

export function getTimeIntervals(interval, startTime, endTime, timeZone) {
  const currentDate = DateTime.now().setZone(timeZone)
  const start = currentDate.set({
    hour: startTime,
    minute: 0,
    second: 0,
    millisecond: 0,
  })
  const end = currentDate.set({
    hour: endTime,
    minute: 30,
    second: 0,
    millisecond: 0,
  })

  const result = []
  let current = DateTime.fromISO(start, { zone: timeZone })

  while (current < end) {
    result.push(current)
    current = current.plus({ minute: interval })
  }

  return result
}

export function getPlatform(flags, user = null, office = null, orders = []) {
  const agent = user?.roles?.includes('Agent') || false
  const officeFlags = office?.flags || []
  const orderFlags = orders?.find((order) => order?.annotation?.flags)?.annotation?.flags || null
  const userFlags = user?.flags || []
  const assetDownloadOverride = userFlags.includes('Can_Not_Download_Proofs') || false
  const assetBookingOverride = userFlags.includes('Can_Not_Book_Appointment') || false
  const assetProofOverride = userFlags.includes('Can_Not_Proof') || false
  const vendorShareableLinkOverride = userFlags.includes('Can_Not_Generate_Vendor_Sharing_Links') || false

  let managedFlags = {
    assetDownload: assetDownloadOverride ? false : officeFlags?.includes('AdminOnlyDownload') ? (agent ? false : true) : true,
    bookingAccess: assetBookingOverride ? false : officeFlags?.includes('AdminOnlyBook') ? (agent ? false : true) : true,
    assetProof: assetProofOverride ? false : true,
    vendorShareGenerateLink: vendorShareableLinkOverride ? false : true,
  }

  if (orderFlags?.campaigntrack_reference || flags?.campaigntrack_reference) {
    return {
      ...managedFlags,
      name: 'Campaigntrack',
      campaigntrackPropertyNo: flags?.campaigntrack_reference,
      campaigntrackOrderNo: orderFlags?.campaigntrack_reference,
      // projectFlags
      bookingContacts: true,
      contentGallery: true,
      href: config.ctProjectReferenceUrl,
      logo: campaigntrack,
      marketplace: false,
      pushContentToPlatform: true,
      shoppingCart: false,
      // userFlags
      editProject: false,
      newProject: false,
      profileUsername: false,
    }
  }

  if (flags?.realhub_reference) {
    return {
      ...managedFlags,
      name: 'Realhub',
      // projectFlags
      bookingContacts: false,
      contentGallery: true,
      logo: realhub,
      marketplace: false,
      pushContentToPlatform: true,
      shoppingCart: false,
      // userFlags
      editProject: false,
      newProject: false,
      profileUsername: false,
    }
  }

  return {
    ...managedFlags,
    // projectFlags
    bookingContacts: false,
    contentGallery: true,
    marketplace: true,
    pushContentToPlatform: officeFlags?.includes('Approval') || false,
    shoppingCart: true,
    // userFlags
    editProject: true,
    newProject: true,
    profileUsername: true,
  }
}

export function getUniquePropertiesWithValues(obj1, obj2) {
  const uniqueProperties = {}

  for (let key in obj1) {
    if (!obj2.hasOwnProperty(key)) {
      uniqueProperties[key] = obj1[key]
    }
  }

  return uniqueProperties
}

const USER_AGENT = navigator.userAgent || navigator.vendor || window.opera

export function isMobileDevice() {
  return /android/i.test(USER_AGENT) || /iPad|iPhone|iPod/.test(USER_AGENT)
}

export function isIOS() {
  return /iPad|iPhone|iPod/.test(USER_AGENT)
}

export function isAndroid() {
  return /android/i.test(USER_AGENT)
}

export function sanitizeForBlobTag(tag) {
  return tag
    .replace(/[^\x20-\x7E]/g, '')       // Keep only printable ASCII
    .replace(/[^\w\s.-]/g, '')          // Remove disallowed characters
    .replace(/[\/\\]/g, '')             // Remove / and \
    .replace(/^\$/, '')                 // Remove leading $
    .substring(0, 128)                  // Truncate to 128 chars
}
